/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Organization Settings Tab Component
 * ============================================================================
 *
 * Displays and manages organization-specific settings that override
 * platform defaults. Settings are grouped by category: Regional, Security,
 * and Branding.
 *
 * ============================================================================
 */

import React, { useState, useMemo, useEffect } from 'react';
import {
  Settings, Globe, Shield, Palette, RefreshCw, Save, X,
  ChevronDown, ChevronRight, Check, AlertCircle, Loader2, RotateCcw, FlaskConical
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  useOrganizationSettings,
  useUpdateOrganizationSettings,
  useResetOrganizationSettings,
} from '@/hooks/api/useOrganizationSettings';
import { OrgSettingItem, OrgSettingCategory } from '@/types/superadmin.types';
import { ConfirmationDialog } from '@/components/ui/ConfirmationDialog';
import { useNotification } from '@/hooks/useNotification';

interface OrganizationSettingsTabProps {
  orgId: string;
  orgName: string;
}

// Category configuration
const CATEGORY_CONFIG: Record<OrgSettingCategory, {
  icon: React.ReactNode;
  color: string;
  bgColor: string;
}> = {
  REGIONAL: {
    icon: <Globe className="w-4 h-4" />,
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/15',
  },
  SECURITY: {
    icon: <Shield className="w-4 h-4" />,
    color: 'text-amber-500',
    bgColor: 'bg-amber-500/15',
  },
  BRANDING: {
    icon: <Palette className="w-4 h-4" />,
    color: 'text-purple-500',
    bgColor: 'bg-purple-500/15',
  },
  FEATURES: {
    icon: <FlaskConical className="w-4 h-4" />,
    color: 'text-cyan-500',
    bgColor: 'bg-cyan-500/15',
  },
};

// Setting input component based on value type
interface SettingInputProps {
  setting: OrgSettingItem;
  value: string | null;
  onChange: (value: string | null) => void;
  disabled?: boolean;
}

const SettingInput: React.FC<SettingInputProps> = ({ setting, value, onChange, disabled }) => {
  const { t } = useTranslation('core');

  if (setting.valueType === 'BOOLEAN') {
    return (
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => onChange(value === 'true' ? 'false' : 'true')}
          disabled={disabled}
          className={`
            relative w-11 h-6 rounded-full transition-colors duration-200
            ${value === 'true' ? 'bg-emerald-500' : 'bg-gray-600'}
            ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          `}
        >
          <span
            className={`
              absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-md
              transition-transform duration-200
              ${value === 'true' ? 'translate-x-5' : 'translate-x-0'}
            `}
          />
        </button>
        <span className="text-sm text-secondary">
          {value === 'true' ? t('common.enabled') : t('common.disabled')}
        </span>
      </div>
    );
  }

  if (setting.valueType === 'INTEGER') {
    return (
      <input
        type="number"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
        disabled={disabled}
        placeholder={setting.defaultValue ?? ''}
        className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground
                   focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary
                   disabled:opacity-50 disabled:cursor-not-allowed"
      />
    );
  }

  // Handle color inputs
  if (setting.key.includes('color')) {
    return (
      <div className="flex items-center gap-3">
        <input
          type="color"
          value={value || setting.defaultValue || '#000000'}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="w-10 h-10 rounded-lg border border-border cursor-pointer
                     disabled:opacity-50 disabled:cursor-not-allowed"
        />
        <input
          type="text"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value || null)}
          disabled={disabled}
          placeholder={setting.defaultValue ?? '#RRGGBB'}
          className="flex-1 px-3 py-2 rounded-lg border border-border bg-background text-foreground
                     font-mono text-sm
                     focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary
                     disabled:opacity-50 disabled:cursor-not-allowed"
        />
      </div>
    );
  }

  // Default string input
  return (
    <input
      type="text"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value || null)}
      disabled={disabled}
      placeholder={setting.defaultValue ?? ''}
      className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground
                 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary
                 disabled:opacity-50 disabled:cursor-not-allowed"
    />
  );
};

// Individual setting row
interface SettingRowProps {
  setting: OrgSettingItem;
  editedValue: string | null | undefined;
  isEditing: boolean;
  onValueChange: (key: string, value: string | null) => void;
  onResetSingle: (key: string) => void;
}

const SettingRow: React.FC<SettingRowProps> = ({
  setting,
  editedValue,
  isEditing,
  onValueChange,
  onResetSingle,
}) => {
  const { t } = useTranslation('core');
  const currentValue = editedValue !== undefined ? editedValue : setting.value;
  const hasLocalChange = editedValue !== undefined && editedValue !== setting.value;

  return (
    <div className={`
      p-4 rounded-lg border transition-all duration-200
      ${setting.isOverridden || hasLocalChange
        ? 'border-primary/30 bg-primary/5'
        : 'border-border bg-background'}
    `}>
      <div className="flex flex-col gap-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-foreground">
                {t(`orgSettings.keys.${setting.key}`, setting.key)}
              </span>
              {(setting.isOverridden || hasLocalChange) && (
                <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-primary/20 text-primary">
                  {hasLocalChange ? t('orgSettings.modified') : t('orgSettings.overridden')}
                </span>
              )}
            </div>
            <p className="text-xs text-secondary mt-0.5">
              {t(`orgSettings.descriptions.${setting.key}`, setting.description)}
            </p>
          </div>
          {setting.isOverridden && !hasLocalChange && (
            <button
              type="button"
              onClick={() => onResetSingle(setting.key)}
              className="p-1.5 rounded-lg text-secondary hover:text-foreground hover:bg-surface
                         transition-colors"
              title={t('orgSettings.resetToDefault')}
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="flex items-center gap-3">
          <div className="flex-1">
            <SettingInput
              setting={setting}
              value={currentValue}
              onChange={(val) => onValueChange(setting.key, val)}
              disabled={!isEditing}
            />
          </div>
        </div>

        {!isEditing && (
          <div className="flex items-center gap-2 text-xs text-secondary">
            <span>{t('orgSettings.effectiveValue')}:</span>
            <code className="px-1.5 py-0.5 rounded bg-surface font-mono">
              {setting.effectiveValue || t('common.notSet')}
            </code>
            {setting.defaultValue && !setting.isOverridden && (
              <span className="text-muted">({t('orgSettings.platformDefault')})</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Category section component
interface CategorySectionProps {
  category: OrgSettingCategory;
  settings: OrgSettingItem[];
  editedValues: Record<string, string | null>;
  isEditing: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  onValueChange: (key: string, value: string | null) => void;
  onResetSingle: (key: string) => void;
}

const CategorySection: React.FC<CategorySectionProps> = ({
  category,
  settings,
  editedValues,
  isEditing,
  isExpanded,
  onToggle,
  onValueChange,
  onResetSingle,
}) => {
  const { t } = useTranslation('core');
  const config = CATEGORY_CONFIG[category];
  const overriddenCount = settings.filter(s => s.isOverridden).length;
  const modifiedCount = settings.filter(s => editedValues[s.key] !== undefined && editedValues[s.key] !== s.value).length;

  return (
    <div className="border border-border rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 bg-surface hover:bg-surface/80
                   transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${config.bgColor} ${config.color}`}>
            {config.icon}
          </div>
          <div className="text-left">
            <h4 className="text-sm font-semibold text-foreground">
              {t(`orgSettings.categories.${category.toLowerCase()}`)}
            </h4>
            <p className="text-xs text-secondary">
              {settings.length} {t('orgSettings.settings')}
              {(overriddenCount > 0 || modifiedCount > 0) && (
                <span className="ml-2 text-primary">
                  ({modifiedCount > 0
                    ? `${modifiedCount} ${t('orgSettings.modified')}`
                    : `${overriddenCount} ${t('orgSettings.overridden')}`
                  })
                </span>
              )}
            </p>
          </div>
        </div>
        {isExpanded ? (
          <ChevronDown className="w-5 h-5 text-secondary" />
        ) : (
          <ChevronRight className="w-5 h-5 text-secondary" />
        )}
      </button>

      {isExpanded && (
        <div className="p-4 space-y-3 bg-background border-t border-border">
          {settings.map((setting) => (
            <SettingRow
              key={setting.key}
              setting={setting}
              editedValue={editedValues[setting.key]}
              isEditing={isEditing}
              onValueChange={onValueChange}
              onResetSingle={onResetSingle}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Main component
export default function OrganizationSettingsTab({ orgId, orgName }: OrganizationSettingsTabProps) {
  const { t } = useTranslation('core');
  const { success, error: showError } = useNotification();

  // Data fetching
  const { data: settingsData, isLoading, error, refetch } = useOrganizationSettings(orgId);
  const updateMutation = useUpdateOrganizationSettings();
  const resetMutation = useResetOrganizationSettings();

  // Local state
  const [isEditing, setIsEditing] = useState(false);
  const [editedValues, setEditedValues] = useState<Record<string, string | null>>({});
  const [expandedCategories, setExpandedCategories] = useState<Set<OrgSettingCategory>>(
    new Set(['REGIONAL', 'SECURITY', 'BRANDING'])
  );
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [showOnlyOverridden, setShowOnlyOverridden] = useState(false);

  // Reset edited values when exiting edit mode
  useEffect(() => {
    if (!isEditing) {
      setEditedValues({});
    }
  }, [isEditing]);

  // Group settings by category
  const groupedSettings = useMemo((): Record<OrgSettingCategory, OrgSettingItem[]> => {
    const grouped: Record<OrgSettingCategory, OrgSettingItem[]> = {
      REGIONAL: [],
      SECURITY: [],
      BRANDING: [],
      FEATURES: [],
    };

    if (!settingsData?.settings) return grouped;

    settingsData.settings.forEach((setting) => {
      if (grouped[setting.category]) {
        if (!showOnlyOverridden || setting.isOverridden || editedValues[setting.key] !== undefined) {
          grouped[setting.category].push(setting);
        }
      }
    });

    return grouped;
  }, [settingsData, showOnlyOverridden, editedValues]);

  // Check if there are unsaved changes
  const hasChanges = useMemo(() => {
    return Object.keys(editedValues).some(
      key => editedValues[key] !== settingsData?.settings.find(s => s.key === key)?.value
    );
  }, [editedValues, settingsData]);

  // Handlers
  const handleValueChange = (key: string, value: string | null) => {
    setEditedValues(prev => ({ ...prev, [key]: value }));
  };

  const handleResetSingle = (key: string) => {
    setEditedValues(prev => ({ ...prev, [key]: null }));
  };

  const handleToggleCategory = (category: OrgSettingCategory) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const handleSave = async () => {
    try {
      await updateMutation.mutateAsync({
        orgId,
        settings: editedValues,
      });
      success(t('orgSettings.saveSuccess'));
      setIsEditing(false);
      setEditedValues({});
    } catch (err) {
      showError(t('orgSettings.saveError'));
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedValues({});
  };

  const handleResetAll = async () => {
    try {
      await resetMutation.mutateAsync(orgId);
      success(t('orgSettings.resetSuccess'));
      setResetDialogOpen(false);
      refetch();
    } catch (err) {
      showError(t('orgSettings.resetError'));
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="bg-surface border border-border rounded-xl p-6">
        <div className="flex items-center justify-center h-32">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-surface border border-border rounded-xl p-6">
        <div className="flex flex-col items-center justify-center h-32 text-center">
          <AlertCircle className="w-8 h-8 text-red-500 mb-2" />
          <p className="text-secondary">{t('orgSettings.loadError')}</p>
          <button
            onClick={() => refetch()}
            className="mt-3 px-4 py-2 text-sm font-medium text-primary hover:bg-primary/10 rounded-lg
                       transition-colors"
          >
            {t('common.retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface border border-border rounded-xl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-5 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/15 flex items-center justify-center text-primary">
            <Settings className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-md font-semibold text-foreground">
              {t('orgSettings.title')}
            </h3>
            <p className="text-xs text-secondary">
              {settingsData?.overriddenCount || 0} {t('orgSettings.overriddenSettings')}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Filter toggle */}
          <label className="flex items-center gap-2 text-sm text-secondary cursor-pointer">
            <input
              type="checkbox"
              checked={showOnlyOverridden}
              onChange={(e) => setShowOnlyOverridden(e.target.checked)}
              className="w-4 h-4 rounded border-border accent-primary"
            />
            {t('orgSettings.showOnlyOverridden')}
          </label>

          {/* Action buttons */}
          {isEditing ? (
            <>
              <button
                onClick={handleCancelEdit}
                disabled={updateMutation.isPending}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-secondary
                           border border-border rounded-lg hover:bg-background transition-colors
                           disabled:opacity-50"
              >
                <X className="w-4 h-4" />
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSave}
                disabled={!hasChanges || updateMutation.isPending}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white
                           bg-primary rounded-lg hover:bg-primary/90 transition-colors
                           disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {updateMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {t('common.save')}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setResetDialogOpen(true)}
                disabled={!settingsData?.overriddenCount}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-secondary
                           border border-border rounded-lg hover:bg-background transition-colors
                           disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw className="w-4 h-4" />
                <span className="hidden sm:inline">{t('orgSettings.resetAll')}</span>
              </button>
              <button
                onClick={() => setIsEditing(true)}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white
                           bg-primary rounded-lg hover:bg-primary/90 transition-colors"
              >
                <Settings className="w-4 h-4" />
                {t('common.edit')}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Settings Content */}
      <div className="p-5 space-y-4">
        {Object.entries(groupedSettings).map(([category, settings]) => (
          settings.length > 0 && (
            <CategorySection
              key={category}
              category={category as OrgSettingCategory}
              settings={settings}
              editedValues={editedValues}
              isEditing={isEditing}
              isExpanded={expandedCategories.has(category as OrgSettingCategory)}
              onToggle={() => handleToggleCategory(category as OrgSettingCategory)}
              onValueChange={handleValueChange}
              onResetSingle={handleResetSingle}
            />
          )
        ))}

        {Object.values(groupedSettings).every(arr => arr.length === 0) && (
          <div className="text-center py-8 text-secondary">
            <Settings className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>{showOnlyOverridden ? t('orgSettings.noOverriddenSettings') : t('orgSettings.noSettings')}</p>
          </div>
        )}
      </div>

      {/* Reset Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={resetDialogOpen}
        variant="warning"
        title={t('orgSettings.resetDialog.title')}
        message={t('orgSettings.resetDialog.message')}
        details={
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-muted">{t('orgSettings.resetDialog.organization')}:</span>
              <span className="font-medium">{orgName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted">{t('orgSettings.resetDialog.overridden')}:</span>
              <span className="font-medium">{settingsData?.overriddenCount || 0}</span>
            </div>
          </div>
        }
        confirmText={t('orgSettings.resetDialog.confirm')}
        isLoading={resetMutation.isPending}
        onConfirm={handleResetAll}
        onCancel={() => setResetDialogOpen(false)}
      />
    </div>
  );
}
